// ⚠ The `.ts` extension is deliberate: this module is exercised by `npm test`, which runs each
// `*.test.ts` under plain `node --experimental-strip-types` — real ESM, which resolves no
// extension for you. `allowImportingTsExtensions` is on and Vite resolves it unchanged.
import { AXIS_LABEL } from './usageAxis.ts'
import { USAGE_EPOCH_DAY } from './usagePeriods.ts'

/** One row of `get_org_usage_series` (router#1165, migration 0164). */
export interface UsageSeriesRow {
  day: string // 'YYYY-MM-DD'
  axis: string
  amount: number
  unit: string
}

/** One axis's series, ready to draw. */
export interface AxisSeries {
  axis: string
  unit: string
  points: readonly { day: string; amount: number }[]
  total: number
  max: number
}

/**
 * Group the flat rows into one series per axis, days ascending.
 *
 * ⚠⚠ THE DAYS SUM TO THE PERIOD, AND THAT IS THE FUNCTION'S CONTRACT RATHER THAN A COINCIDENCE.
 * Migration `0164` accumulates to each day's cut and DIFFERENCES, so `total` here equals the
 * figure `get_org_usage` reports for the same period. It is why the chart can carry a total at
 * all: a naive per-day call would pay compute's one-minute minimum and its ceiling once per DAY,
 * and a task straddling midnight would be billed two minimums for one minute of work.
 *
 * ⚠⚠ TWO AXES, AND STORAGE IS DELIBERATELY NOT THE THIRD. Storage is a LEVEL, not a flow: it is
 * billed as a period max-of-daily-max per subject, summed across subjects, so a daily storage
 * series does not SUM to its billed figure — it MAXES to it. `0164` leaves it out so this
 * contract stays true for every axis it returns, and nothing here should add it: stacking it
 * beside two additive axes would invite a reader to add it up. `nodes` is out for the same class
 * of reason — a monthly count of accrual rows, not a quantity that accumulates.
 *
 * ⚠ The unit is taken from the row, never from the axis name. `get_org_usage_series` denominates
 * bandwidth in bytes and compute in CENTS, for the reason `usageAxis.ts` gives at length: compute
 * is quoted per hour by machine class and drawn against one shared dollar allowance, so no number
 * of hours names what is left. A byte formatter prints "1.2 KB" for twelve dollars.
 */
export const seriesByAxis = (rows: readonly UsageSeriesRow[]): AxisSeries[] => {
  const byAxis = new Map<string, { unit: string; points: { day: string; amount: number }[] }>()
  for (const row of rows) {
    if (!row || typeof row.axis !== 'string') continue
    const amount = Number.isFinite(row.amount) ? row.amount : 0
    const entry = byAxis.get(row.axis) ?? { unit: row.unit, points: [] }
    entry.points.push({ day: row.day, amount })
    byAxis.set(row.axis, entry)
  }
  return [...byAxis.entries()]
    .map(([axis, { unit, points }]) => {
      const ordered = [...points].sort((a, b) => a.day.localeCompare(b.day))
      return {
        axis,
        unit,
        points: ordered,
        total: ordered.reduce((sum, p) => sum + p.amount, 0),
        max: ordered.reduce((peak, p) => Math.max(peak, p.amount), 0),
      }
    })
    .sort((a, b) => a.axis.localeCompare(b.axis))
}

/**
 * Whether an axis has anything worth drawing.
 *
 * ⚠⚠ NOT `points.length > 0`. The RPC returns a row for EVERY day of the period to today, zero
 * included — which is what makes a length check useless as
 * evidence that anything happened. Without this, an organization that used nothing gets a
 * full-width chart of a flat zero line, which reads as a broken chart rather than as an empty
 * month. Same rule `ComputeUsage` applies to its own all-zero table.
 */
export const hasSeriesUsage = (series: AxisSeries): boolean => series.max > 0

/** The axes worth rendering: those that actually moved, in a stable order. */
export const drawableSeries = (rows: readonly UsageSeriesRow[]): AxisSeries[] =>
  seriesByAxis(rows).filter(hasSeriesUsage)

/**
 * Every UTC day of one period, ascending, as `YYYY-MM-DD`. An unreadable period gives no days.
 *
 * ⚠ UTC, for the reason `formatSeriesDay` states: the period is a UTC calendar month.
 */
export const periodDays = (period: string): string[] => {
  const match = /^(\d{4})-(\d{2})$/.exec(period)
  if (!match) return []
  const year = Number(match[1])
  const month = Number(match[2])
  if (month < 1 || month > 12) return []
  // Day 0 of the NEXT month is the last day of this one.
  const count = new Date(Date.UTC(year, month, 0)).getUTCDate()
  return Array.from({ length: count }, (_, i) => `${period}-${String(i + 1).padStart(2, '0')}`)
}

/** One slot of the chart. `amount` is null for a day the series does not hold. */
export interface SeriesSlot {
  day: string
  amount: number | null
}

/**
 * One slot for EVERY day of the period, for one axis. ⚠ It is what the chart draws when it draws
 * the period ALONE: a past period, the last day of a month, or no month before. On the other days
 * the chart draws `windowSlots`, which has no day to come.
 *
 * ⚠⚠ THE SERIES STOPS AT TODAY AND THE CHART MUST NOT. `get_org_usage_series` returns a row for
 * each day from the period start to today (migration `0164`: "the range stops at today"). The
 * chart divided its width among the rows it had, so on the first day of a month one day filled
 * the whole chart, and on the second each day filled half (measured on prod 2026-10-02). A day
 * then had a different width on each day of the month. With one slot per day of the period, a day
 * has one width and the empty part of the chart is the part of the month that has not come.
 *
 * ⚠ A day to come is `null`, never 0. Zero says "nothing happened that day", and the chart draws
 * a baseline for it. A day that has not come has no reading, and the chart draws nothing.
 *
 * ⚠ A series with no day in the period is returned as it is. The slots would all be empty, and a
 * chart of nothing under a total is worse than a chart on its own days.
 */
export const periodSlots = (series: AxisSeries, period: string): SeriesSlot[] => {
  const held = new Map(series.points.map(p => [p.day, p.amount]))
  const slots = periodDays(period).map(day => ({ day, amount: held.get(day) ?? null }))
  return slots.some(s => s.amount !== null) ? slots : series.points.map(p => ({ ...p }))
}

/** The smallest of 1, 2, 5 or 10 times a power of ten that is not below `raw`. */
const niceStep = (raw: number): number => {
  const power = Math.pow(10, Math.floor(Math.log10(raw)))
  const factor = raw / power
  return (factor <= 1 ? 1 : factor <= 2 ? 2 : factor <= 5 ? 5 : 10) * power
}

/** One day of the chart. `amount` is null for a day with no reading; `current` is false for a day
 *  of the month before, which the chart draws in grey. */
export interface WindowSlot {
  day: string
  amount: number | null
  current: boolean
}

const DAY_MS = 86_400_000

/**
 * The days the panel's charts draw: the period to its last day with a reading, and in front of it
 * the last days of the month before, so a chart is always full (Benoit, 2026-10-02). Null when the
 * charts draw the period alone.
 *
 * On the 2nd of November the days are the 4th of October to the 2nd of November: 30 days, 28 of
 * them of the month before. On the 30th they are November alone, and the answer is null. A past
 * period holds all its days, so it is null too.
 *
 * ⚠⚠ THE HISTORY, SO IT IS NOT DONE AGAIN. The chart first had one slot for each day of the period
 * (`periodSlots`), and early in a month it was an empty box with one or two bars. Then the month
 * before was drawn in grey BEHIND the period, the 4th behind the 4th: two bars in each slot, which
 * was busy, and the weekends of the two months were not on the same slots. One line of days in
 * their order has one bar a day and one calendar.
 *
 * ⚠⚠ ONE ANSWER FOR THE PANEL, NOT ONE FOR EACH AXIS. It takes every axis the panel draws and
 * every axis of the month before. The caption says "the last N days" once, and a day must be at
 * one place in each chart. Worked out for each axis apart, an axis with no row of the month before
 * drew the period alone beside a chart that did not.
 *
 * ⚠ The number of days is the number of days of the PERIOD, 28 to 31, not 31. So a past period and
 * the last day of a month are that month and no more.
 *
 * ⚠ The end is the last day of the period that a series HOLDS, not the clock. The RPC returns a
 * row for each day from the period start to today, so the two are the same, and a function with no
 * clock is one a test can call.
 *
 * ⚠ A day before the first day the month before holds is not drawn: the 1st of March goes back to
 * the 1st of February, 29 days and not 31 with two empty ones. The chart reads one month back.
 *
 * ⚠⚠ NOR A DAY BELOW `USAGE_EPOCH_DAY`. The go-live reset deleted all usage before the 12th of
 * September 2026, and the RPC returns a row of zero for each of those days. A grey day of "0 B"
 * says "you used nothing" about a day we hold no record of. So on the 2nd of October 2026 the days
 * are the 12th of September to the 2nd of October: 21 days, and the chart is still full.
 *
 * ⚠ Null also when no day of the month before is left to draw, and when no series holds a day of
 * the period.
 */
export const windowDays = (
  series: readonly AxisSeries[],
  previous: readonly AxisSeries[],
  period: string
): string[] | null => {
  const days = periodDays(period)
  const held = new Set(series.flatMap(s => s.points.map(p => p.day)))
  const reached = days.filter(day => held.has(day))
  const before = previous.flatMap(s => s.points.map(p => p.day))
  if (reached.length === 0 || reached.length === days.length || before.length === 0) return null

  const end = Date.parse(`${reached[reached.length - 1]}T00:00:00Z`)
  const first = before.reduce((a, b) => (b < a ? b : a))
  const floor = first > USAGE_EPOCH_DAY ? first : USAGE_EPOCH_DAY
  const window = Array.from({ length: days.length }, (_, i) =>
    new Date(end - (days.length - 1 - i) * DAY_MS).toISOString().slice(0, 10)
  ).filter(day => day >= floor)
  return window[0] < days[0] ? window : null
}

/**
 * One axis's amounts on the panel's days — see `windowDays`. A day of the period is `current`; a
 * day of the month before is not, and the chart draws it in grey. With `days` null the slots are
 * `periodSlots`: the period alone, with its days to come empty.
 *
 * ⚠ A day the axis holds no row for is null, in the period or before it: no reading, and the chart
 * draws nothing. Zero is a reading.
 */
export const windowSlots = (
  series: AxisSeries,
  previous: AxisSeries | null | undefined,
  period: string,
  days: readonly string[] | null
): WindowSlot[] => {
  if (days === null) return periodSlots(series, period).map(slot => ({ ...slot, current: true }))
  const start = periodDays(period)[0]
  const held = new Map(series.points.map(p => [p.day, p.amount]))
  const before = new Map((previous?.points ?? []).map(p => [p.day, p.amount]))
  return days.map(day =>
    day >= start
      ? { day, amount: held.get(day) ?? null, current: true }
      : { day, amount: before.get(day) ?? null, current: false }
  )
}

/**
 * Where the period starts in a chart that has grey days before it, as a fraction of the chart's
 * width: the centre of the first slot in colour. Null when the label must not be drawn.
 *
 * The chart names that day under the bars ("Oct 1"). ⚠ Only in the middle half of the chart: the
 * two end labels are at the edges, and nearer to an edge the three are on top of each other.
 */
export const periodStartAt = (slots: readonly { current: boolean }[]): number | null => {
  const start = slots.findIndex(slot => slot.current)
  if (start <= 0) return null
  const at = (start + 0.5) / slots.length
  return at >= 0.25 && at <= 0.75 ? at : null
}

/** The busiest day a chart draws, in colour or in grey. 0 when no day moved. */
export const slotsPeak = (slots: readonly { amount: number | null }[]): number =>
  slots.reduce((peak, slot) => Math.max(peak, slot.amount ?? 0), 0)

/**
 * The marks on a chart's scale for one busiest day, ascending, in the axis's own unit. The last
 * one is the top of the chart. Zero is not a mark: the bottom of the chart is zero.
 *
 * ⚠⚠ THE SCALE FOLLOWS THE DATA, AND THE CHART PRINTS IT (Benoit, 2026-10-02). The top is the
 * busiest day rounded up to a round amount, and there are one to three marks, each a round amount:
 * 370 MB gives 200 MB and 400 MB; 12.4 GB gives 5, 10 and 15 GB. So a reader reads the amount of
 * each day from the chart, and two charts of different amounts do not look the same.
 *
 * ⚠ `peak` is the busiest day the chart DRAWS (`slotsPeak`), a grey day too, so no bar is cut.
 *
 * ⚠ The history, so it is not done again. The first chart scaled to the busiest day and printed
 * nothing, so 0.6 cents of compute drew as tall as 115.1 GB of bandwidth. app#445 put one day's
 * share of the allowance in the scale to answer that, and an org far below its allowance got a
 * flat chart: 383.6 MB drew bars of about 4 px and 1 px in 96 px. The allowance is now a LINE on
 * the chart (`seriesPace`), never a part of the scale.
 *
 * ⚠ A step is never less than 1. Bandwidth is whole bytes, and compute is cents that print as whole
 * cents, so a smaller step gives two marks with one label. A day of 0.6 cents is drawn at 60 % of
 * a chart whose one mark is $0.01.
 *
 * ⚠ Round in base 10 because `formatBytes` divides by 1000: 200,000,000 prints "200.0 MB".
 *
 * ⚠ Never empty, and the top is never 0: the caller divides by it.
 */
export const scaleTicks = (peak: number): number[] => {
  if (!(peak > 0) || !Number.isFinite(peak)) return [1]
  const step = Math.max(1, niceStep(peak / 3))
  const count = Math.max(1, Math.ceil(peak / step))
  return Array.from({ length: count }, (_, i) => step * (i + 1))
}

/**
 * One day's even share of the period's allowance: the amount a day uses when the allowance lasts
 * exactly the period. Null when there is no share to draw.
 *
 * The chart draws it as a dotted line, so the reader sees each day against the allowance (Benoit,
 * 2026-10-02). ⚠ It is a line and not the scale: see `scaleTicks`. ⚠ It is the allowance of the
 * PERIOD over the days of the PERIOD, and it is drawn across the grey days of the month before too:
 * the line says what a day of this period is worth, and the grey days are there to compare with.
 * When it is above the top of the chart the chart says so in words and draws no line.
 *
 * ⚠ `included` has THREE states (see `allowanceFraction`). Null is unlimited and 0 is a real
 * allowance of zero. Neither gives a share to compare with, and "not loaded" gives none either.
 */
export const seriesPace = (included: number | null | undefined, days: number): number | null => {
  if (included == null || !Number.isFinite(included) || included <= 0) return null
  if (!Number.isFinite(days) || days <= 0) return null
  return included / days
}

/**
 * A day's label for the axis beneath the chart.
 *
 * ⚠⚠ UTC, ALWAYS. `day` is a DATE from a UTC calendar month, not an instant — the billing period
 * is a UTC month and the router's `PeriodBounds` agrees. A plain `toLocaleDateString` on
 * '2026-09-01' parses it as UTC midnight and then renders it in the reader's zone, printing
 * "31 Aug" for everyone west of Greenwich: the wrong DAY, on a screen about money. The same rule
 * as `freshness.ts` — a boundary is UTC, an instant that occurred is local.
 */
export const formatSeriesDay = (day: string): string =>
  new Date(`${day}T00:00:00Z`).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  })

/**
 * Whether a day is a Saturday or a Sunday.
 *
 * The chart draws the bar of such a day lighter, so a reader finds the weeks in a month of bars
 * (Benoit, 2026-10-02).
 *
 * ⚠ It is asked of the bar's own date and the mark is on the BAR, in colour or in grey. It was a
 * band on the slot while a slot held a day of each of two months, and that was right for one of
 * them only.
 *
 * ⚠ UTC, for the reason `formatSeriesDay` states: `day` is a date of a UTC calendar month. A local
 * `getDay()` gives Friday for a Saturday to each reader west of Greenwich.
 */
export const isWeekend = (day: string): boolean => {
  const weekday = new Date(`${day}T00:00:00Z`).getUTCDay()
  return weekday === 0 || weekday === 6
}

/**
 * Human label for an axis. Unknown axes render their own name rather than a guess.
 *
 * ⚠ `AXIS_LABEL` is the one definition, shared with the rings and the bars on the same screen. It
 * was spelled twice, and a second spelling is how the next correction reaches only one of them.
 */
export const seriesAxisLabel = (axis: string): string => AXIS_LABEL[axis] ?? axis

/**
 * Whether an axis's drawn days still add up to the figure BILLED for the same period.
 *
 * ⚠⚠ THE DIVERGENCE THIS WAS WRITTEN AGAINST IS FIXED, AND THIS COMMENT DESCRIBED THE OLD WORLD
 * FOR AS LONG AS IT STOOD. It said `get_org_usage_series` reads `node_metrics_unified` — the daily
 * rollup UNION the raw tail — while `get_org_usage`, which draws the bar, the ring and the
 * projected bill above this chart, reads RAW `node_metrics`; that `0028` prunes raw at the current
 * UTC month plus three and never prunes the rollup; and that the picker's two oldest months
 * therefore outlive the records the bar is measured from, drawing gigabytes under a bar reading
 * 0 B from 2026-10-01. Every clause was true when written and the last one is now false:
 * **router migration `0167` moved `get_org_usage`, `get_billable_usage_admin` and
 * `get_usage_freshness` onto `node_metrics_unified`** ([router#1269](https://github.com/nobgp/nobgp-router/issues/1269),
 * closed 2026-09-05). Verified against prod 2026-09-12 by regexing the LIVE function bodies:
 * `get_org_usage` holds one `node_metrics_unified` reference and zero raw ones. Both arms read one
 * source, so retention cannot separate them and no date is pending.
 *
 * ⚠ SO THIS GUARD OUTLIVED ITS CAUSE AND IS KEPT ANYWAY, which is a decision and not an oversight.
 * The two arms are still two queries over one source — the series cuts by UTC day and stops at
 * today, the bar sums the period — so they can still disagree through a mid-day rollup lag or a
 * partial period. What changed is that a difference no longer has a KNOWN explanation, which makes
 * naming it more useful than before, not less: the caption states the sum only where it holds and
 * reports the difference where it does not, and it never predicts a date.
 *
 * ⚠ MATERIAL, not exact, and the threshold is deliberately loose. The last measurement that
 * compared the two arms — prod 2026-09-05, difference 0 on every month the picker then offered —
 * cannot be repeated and is not quoted here: `go_live_reset()` emptied every one of those months
 * on 2026-09-12, and `recentPeriods` no longer offers them. So the tolerance is set for what the
 * two arms can still do rather than for a measured spread: they read one source and can separate
 * only through a mid-day rollup lag or a partial period, both small, and the point of a 1 % floor
 * is that a small disagreement this function cannot explain must not put an alarming sentence on
 * a live month. The case it exists to catch is a chart of gigabytes beside a bar of zero, which
 * no threshold can miss.
 *
 * `billed == null` means the caller passed no figure to compare against — nothing is claimed, so
 * nothing is contradicted.
 */
export const seriesReconciles = (series: AxisSeries, billed: number | null | undefined): boolean => {
  if (billed == null || !Number.isFinite(billed)) return true
  return Math.abs(series.total - billed) <= Math.max(1, Math.abs(billed) * 0.01)
}
